import React, { useEffect, useState } from 'react';
import api from '../api/api';
import { RestaurantAttendance, RestaurantPage } from '../types/restaurantPersonnel';
import { extractApiErrorMessage } from '../utils/apiError';
import { formatCivilDateTime, formatHours } from '../utils/restaurantPersonnelFormat';

const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};
export const attendanceMonthRange = (month: string) => {
  const [year, number] = month.split('-').map(Number);
  return { dateFrom: `${month}-01`,
    dateTo: `${number === 12 ? year + 1 : year}-${String(number === 12 ? 1 : number + 1).padStart(2, '0')}-01` };
};

const RestaurantAttendanceHistory: React.FC<{
  path: string;
  refreshKey?: number;
  onEdit?: (item: RestaurantAttendance) => void;
  disabled?: boolean;
}> = ({ path, refreshKey, onEdit, disabled }) => {
  const [month, setMonth] = useState(currentMonth);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<RestaurantPage<RestaurantAttendance> | null>(null);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setResult(null);
    setError('');
    void api.get<RestaurantPage<RestaurantAttendance>>(path, {
      signal: controller.signal, params: { ...attendanceMonthRange(month), page, pageSize: 25 },
    }).then(response => {
      if (controller.signal.aborted) return;
      setResult(response.data);
    }).catch(failure => {
      if (!controller.signal.aborted)
        setError(extractApiErrorMessage(failure, 'Не вдалося завантажити інтервали табелю.'));
    });
    return () => controller.abort();
  }, [path, month, page, refreshKey, reload]);

  return <section aria-label="Історія табелю">
    <label>Місяць табелю<input type="month" required value={month} disabled={disabled}
      onChange={event => {
        if (/^\d{4}-\d{2}$/.test(event.target.value)) { setMonth(event.target.value); setPage(1); }
      }} /></label>
    <button type="button" disabled={disabled} onClick={() => setReload(value => value + 1)}>Оновити табель</button>
    <details>
      <summary>Детальніше про період табелю</summary>
      <p>За робочою датою, від першого числа включно до першого числа наступного місяця виключно.
        Відкрита зміна незалежно від місяця доступна у «Моє табелювання».</p>
    </details>
    {error && <p role="alert">{error}</p>}
    {!result && !error && <p role="status">Завантаження табелю…</p>}
    {result && <>
      <p role="status">Інтервалів: {result.total} · Сторінка {result.page}</p>
      {!result.items.length && <p>Інтервалів за обраний місяць немає.</p>}
      <div className="restaurant-personnel__list">
        {result.items.map(item => {
          const text = `${formatCivilDateTime(item.checkInAt)} — ${
            item.checkOutAt ? formatCivilDateTime(item.checkOutAt) : 'Відкрито'
          } · ${formatHours(item.hoursWorked)} · коеф. ${item.hourCoefficient} ${item.comment}`;
          return onEdit
            ? <button key={item.id} type="button" disabled={disabled} onClick={() => onEdit(item)}>{text}</button>
            : <p key={item.id}>{text}</p>;
        })}
      </div>
      <div className="restaurant-personnel__actions">
        <button type="button" disabled={disabled || page === 1} onClick={() => setPage(page - 1)}>Попередні інтервали</button>
        <button type="button" disabled={disabled || page * result.pageSize >= result.total}
          onClick={() => setPage(page + 1)}>Наступні інтервали</button>
      </div>
    </>}
  </section>;
};
export default RestaurantAttendanceHistory;
